import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { apiEndPoints } from "../../../helpers/constants";
import { BASE_URL } from "../../../config";
import { authHeaders } from "../../../helpers/axios";
import { notify, notifyAPIMessages } from "../../../helpers/commonFunctions";
import axios from "axios";

export const reqtoGetProfile = createAsyncThunk(
  "profile/reqtoGetProfile",
  async ({ onSuccess, onFailure } = {}, thunkAPI) => {
    try {
      const response = await axios.get(
        BASE_URL + apiEndPoints.profileData,
        authHeaders()
      );
      onSuccess?.(response);

      return response.data;
    } catch (error) {
      const errMsg = error?.response?.data?.message || "Network Error";
      const statusCode = error?.response?.status;

      notify(errMsg, "error");
      onFailure?.(error);

      return thunkAPI.rejectWithValue({
        message: errMsg,
        status: statusCode,
      });
    }
  }
);

export const reqtoToggleTwoFA = createAsyncThunk(
  "profile/reqtoToggleTwoFA",
  async ({ onSuccess, onFailure } = {}, thunkAPI) => {
    try {
      const response = await axios.post(
        BASE_URL + apiEndPoints.twofaToggle,
        {},
        authHeaders()
      );
      onSuccess?.(response);

      return response.data;
    } catch (error) {
      const errMsg = error?.response?.data?.message || "Network Error";
      const statusCode = error?.response?.status;

      notify(errMsg, "error");
      onFailure?.(error);

      return thunkAPI.rejectWithValue({
        message: errMsg,
        status: statusCode,
      });
    }
  }
);

export const reqtoTwoFASetup = createAsyncThunk(
  "profile/reqtoTwoFASetup",
  async ({ onSuccess, onFailure } = {}, thunkAPI) => {
    try {
      const response = await axios.get(
        BASE_URL + apiEndPoints.twofaSetup,
        authHeaders()
      );
      onSuccess?.(response);

      return response.data;
    } catch (error) {
      const errMsg = error?.response?.data?.message || "Network Error";
      const statusCode = error?.response?.status;

      notify(errMsg, "error");
      onFailure?.(error);

      return thunkAPI.rejectWithValue({
        message: errMsg,
        status: statusCode,
      });
    }
  }
);

export const reqtoTwoFAVerify = createAsyncThunk(
  "profile/reqtoTwoFAVerify",
  async ({ token, action, onSuccess, onFailure } = {}, thunkAPI) => {
    try {
      const payload = action ? { token, action } : { token };

      const response = await axios.post(
        BASE_URL + apiEndPoints.twofaVerify,
        payload,
        authHeaders()
      );

      onSuccess?.(response);

      return response.data;
    } catch (error) {
      const errMsg = error?.response?.data?.message || "Network Error";
      const statusCode = error?.response?.status;
      notify(errMsg, "error");
      onFailure?.(error);

      return thunkAPI.rejectWithValue({
        message: errMsg,
        status: statusCode,
      });
    }
  }
);

export const reqToVerifyOtp = createAsyncThunk(
  "reqToVerifyOtp",
  async ({ data, onSuccess, onFailure }) => {
    try {
      const response = await axios.post(
        BASE_URL + apiEndPoints.verifyOtp,
        data,
        authHeaders()
      );
      onSuccess(response);
      return response;
    } catch (error) {
      notify(error?.response?.data?.message || "Network Error", "error");
      onFailure(error);
      return error;
    }
  }
);

export const reqToUpdateProfile = createAsyncThunk(
  "profile/reqToUpdateProfile",
  async ({ data, onSuccess, onFailure } = {}, thunkAPI) => {
    try {
      const response = await axios.post(
        BASE_URL + apiEndPoints.updateProfile,
        data,
        authHeaders()
      );

      onSuccess?.(response);

      notify("Profile updated successfully", "success");

      return response.data;
    } catch (error) {
      const errMsg = error?.response?.data?.message || "Network Error";
      const statusCode = error?.response?.status;

      notify(errMsg, "error");
      onFailure?.(error);

      return thunkAPI.rejectWithValue({
        message: errMsg,
        status: statusCode,
      });
    }
  }
);

export const reqToForgotPassword = createAsyncThunk(
  "reqToForgotPassword",
  async ({ data, onSuccess, onFailure }) => {
    try {
      const response = await axios.post(
        BASE_URL + apiEndPoints.forgotPassword,
        data,
        authHeaders()
      );
      onSuccess(response);
      return response;
    } catch (error) {
      notify(error?.response?.data?.message || "Network Error", "error");
      onFailure(error);
      return error;
    }
  }
);

export const reqToResetPassword = createAsyncThunk(
  "reqToResetPassword",
  async ({ data, onSuccess, onFailure }) => {
    try {
      const response = await axios.post(
        BASE_URL + apiEndPoints.resetPassword,
        data,
        authHeaders()
      );
      onSuccess(response);
      return response;
    } catch (error) {
      notify(error?.response?.data?.message || "Network Error", "error");
      onFailure(error);
      return error;
    }
  }
);

const AuthenticationSlice = createSlice({
  name: "authentication",
  initialState: {
    value: [],
    status: "idle",
    error: null,
    toggleStatus: "idle",
    toggleError: null,
    setupStatus: "idle",
    setupData: null,
    setupError: null,
    verifyStatus: "idle",
    verifyError: null,
    token: null,
  },
  reducers: {
    setLoginSource(state, action) {
      state.loginSource = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(reqtoGetProfile.pending, (state) => {
        state.status = "loading";
      })
      .addCase(reqtoGetProfile.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.value = action.payload;
      })
      .addCase(reqtoGetProfile.rejected, (state, action) => {
        state.status = "failed";
        state.error =
          action.payload?.message || action.error?.message || "Unknown error";
      })

      .addCase(reqtoToggleTwoFA.pending, (state) => {
        state.toggleStatus = "loading";
      })
      .addCase(reqtoToggleTwoFA.fulfilled, (state, action) => {
        state.toggleStatus = "succeeded";

        if (state.value?.data) {
          state.value.data.twofa_enabled = action.payload?.data?.twofa_enabled;
        }
      })
      .addCase(reqtoToggleTwoFA.rejected, (state, action) => {
        state.toggleStatus = "failed";
        state.toggleError =
          action.payload?.message || action.error?.message || "Unknown error";
      })

      .addCase(reqtoTwoFASetup.pending, (state) => {
        state.setupStatus = "loading";
        state.setupData = null;
      })
      .addCase(reqtoTwoFASetup.fulfilled, (state, action) => {
        state.setupStatus = "succeeded";
        state.setupData = action.payload?.data;
      })
      .addCase(reqtoTwoFASetup.rejected, (state, action) => {
        state.setupStatus = "failed";
        state.setupError =
          action.payload?.message || action.error?.message || "Unknown error";
      })

      // POST Verify 2FA
      .addCase(reqtoTwoFAVerify.pending, (state) => {
        state.verifyStatus = "loading";
      })
      .addCase(reqtoTwoFAVerify.fulfilled, (state, action) => {
        state.verifyStatus = "succeeded";
        if (action.payload?.data?.verified) {
          if (state.value?.data) {
            state.value.data.twofa_enabled = true;
          }
        }
      })
      .addCase(reqtoTwoFAVerify.rejected, (state, action) => {
        state.verifyStatus = "failed";
        state.verifyError =
          action.payload?.message || action.error?.message || "Unknown error";
      })

      .addCase(reqToUpdateProfile.pending, (state) => {
        state.status = "loading";
      })
      .addCase(reqToUpdateProfile.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.value = action.payload;
      })
      .addCase(reqToUpdateProfile.rejected, (state, action) => {
        state.status = "failed";
        state.error =
          action.payload?.message || action.error?.message || "Unknown error";
      });

    // FORGOT PASSWORD
    builder.addCase(reqToForgotPassword.pending, (state) => {
      state.loader = true;
    });
    builder.addCase(reqToForgotPassword.fulfilled, (state, action) => {
      state.loader = false;
    });
    builder.addCase(reqToForgotPassword.rejected, (state, action) => {
      state.loader = false;
    });

    // VERIFY OTP
    builder.addCase(reqToVerifyOtp.pending, (state) => {
      state.loader = true;
    });
    builder.addCase(reqToVerifyOtp.fulfilled, (state, action) => {
      state.loader = false;
      state.user = action.payload?.data?.data || null;
      state.token = action.payload?.data?.data?.token;
      localStorage.setItem("token", action.payload?.data?.data?.token);
      state.loginSource = "verifyOtp";
    });
    builder.addCase(reqToVerifyOtp.rejected, (state, action) => {
      state.loader = false;
    });

    // RESET PASSWORD
    builder.addCase(reqToResetPassword.pending, (state) => {
      state.loader = true;
    });
    builder.addCase(reqToResetPassword.fulfilled, (state, action) => {
      state.loader = false;
      // state.loginSource = null;
      // state.user =null
    });
    builder.addCase(reqToResetPassword.rejected, (state, action) => {
      state.loader = false;
      state.error = action.payload;
      notifyAPIMessages(action.payload);
    });
  },
});

export const { setLoginSource } = AuthenticationSlice.actions;

export default AuthenticationSlice.reducer;
